/**
 * Profile title card — same visual language as My Profile (photo, name, email, member since).
 */

import React from 'react';
import { Camera, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../hooks/useTheme';
import { useAppLocale } from '../../providers/LocaleProvider';
import { formatAppDate } from '../../utils/localeDateFormat';
import { darkenColor } from '../../utils/colorUtils';
import { mirrorLinearGradientAngle, mirrorRtlDecor } from '../../utils/mirrorRtlDecor';
import { RtlDecorLayer } from './RtlDecorLayer';

export type LearnerProfileHeroUser = {
  profilePhoto?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  membershipDate?: string | null;
  createdAt?: string | null;
};

export interface LearnerProfileActionButtonsProps {
  onEditProfile?: () => void;
  onChangePassword?: () => void;
  className?: string;
}

/** Edit / change-password pair. Sticky toolbars and the hero card share this markup. */
export const LearnerProfileActionButtons: React.FC<LearnerProfileActionButtonsProps> = ({
  onEditProfile,
  onChangePassword,
  className = '',
}) => {
  const { t: tProfile } = useTranslation('profile');
  return (
    <div
      className={`flex flex-row items-stretch justify-center gap-2 ${className}`.trim()}
    >
      <button
        type="button"
        onClick={onEditProfile}
        className="min-w-0 flex-1 rounded-xl bg-white/95 px-3 py-2 text-center text-xs font-semibold leading-tight text-violet-800 shadow-md ring-1 ring-violet-200/80 transition hover:bg-white dark:bg-gray-900/80 dark:text-violet-200 dark:ring-violet-600/60 dark:hover:bg-gray-900 sm:flex-none sm:px-5 sm:py-2.5 sm:text-sm"
      >
        {tProfile('profileHero.editProfile')}
      </button>
      <button
        type="button"
        onClick={onChangePassword}
        className="min-w-0 flex-1 rounded-xl bg-violet-950/10 px-3 py-2 text-center text-xs font-semibold leading-tight text-violet-950 backdrop-blur-sm ring-1 ring-violet-950/15 transition hover:bg-violet-950/15 dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15 sm:flex-none sm:px-5 sm:py-2.5 sm:text-sm"
      >
        {tProfile('profileHero.changePassword')}
      </button>
    </div>
  );
};

export interface LearnerProfileHeroCardProps {
  user: LearnerProfileHeroUser | null | undefined;
  /** Show edit / change-password buttons (My Profile). */
  showActions?: boolean;
  onEditProfile?: () => void;
  onChangePassword?: () => void;
  /** Show camera control for photo upload (My Profile). */
  showPhotoUpload?: boolean;
  uploadingPhoto?: boolean;
  photoInputId?: string;
  photoAccept?: string;
  onPhotoChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

function makeGradient(isDark: boolean, isRtl: boolean, ...stops: string[]) {
  if (!stops.length) return undefined;
  const adjusted = stops.map((stop) => (isDark ? darkenColor(stop, 0.35) : stop));
  const angle = mirrorLinearGradientAngle(135, isRtl);
  return `linear-gradient(${angle}deg, ${adjusted.join(', ')})`;
}

export const LearnerProfileHeroCard: React.FC<LearnerProfileHeroCardProps> = ({
  user,
  showActions = false,
  onEditProfile,
  onChangePassword,
  showPhotoUpload = false,
  uploadingPhoto = false,
  photoInputId = 'learner-profile-photo-upload',
  photoAccept = 'image/*',
  onPhotoChange,
  className = '',
}) => {
  const { t: tProfile } = useTranslation('profile');
  const { isDark } = useTheme();
  const { direction, locale } = useAppLocale();
  const isRtl = direction === 'rtl';
  const rtlDecor = (cls: string) => mirrorRtlDecor(cls, isRtl);

  const displayName =
    user?.firstName || user?.lastName
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : tProfile('profileHero.welcomeFallback');

  const memberDateRaw = user?.membershipDate || user?.createdAt;
  const memberDate = memberDateRaw
    ? formatAppDate(memberDateRaw, locale, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  return (
    <div className={`clay-card w-full overflow-hidden p-1 dark:border dark:border-gray-700 sm:p-1.5 ${className}`}>
      <div
        className="relative overflow-hidden rounded-xl px-4 py-5 text-center sm:px-6 sm:py-6 sm:text-start"
        dir={direction}
        style={{
          background: isDark
            ? makeGradient(isDark, isRtl, '#4c1d95', '#5b21b6', '#1e1b4b')
            : makeGradient(isDark, isRtl, '#faf5ff', '#e9d5ff', '#c4b5fd'),
        }}
      >
        <RtlDecorLayer
          className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/30 dark:bg-white/10"
          aria-hidden
        />
        <RtlDecorLayer
          className="absolute -left-10 bottom-0 h-36 w-36 rounded-full bg-fuchsia-500/15 dark:bg-violet-400/10"
          aria-hidden
        />
        <div className="relative flex flex-col items-center gap-5 md:flex-row md:items-center md:gap-8">
          <div className="relative shrink-0">
            <div className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-2xl bg-white/90 p-1 shadow-md ring-1 ring-white/60 dark:bg-black/30 dark:ring-white/10 sm:h-[6.5rem] sm:w-[6.5rem] md:h-[7.5rem] md:w-[7.5rem]">
              <div
                className={`relative h-full w-full overflow-hidden rounded-xl ${rtlDecor('bg-gradient-to-br from-purple-400 to-pink-400')}`}
              >
                {user?.profilePhoto ? (
                  <img
                    key={user.profilePhoto}
                    src={user.profilePhoto}
                    alt=""
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    data-error-handled="true"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <User className="h-10 w-10 text-white sm:h-12 sm:w-12 md:h-14 md:w-14" aria-hidden />
                  </div>
                )}
              </div>
            </div>
            {showPhotoUpload ? (
              <>
                <button
                  type="button"
                  onClick={() => document.getElementById(photoInputId)?.click()}
                  disabled={uploadingPhoto}
                  className="absolute -bottom-1 -end-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-purple-200 bg-white text-gray-900 shadow-lg hover:border-purple-300 disabled:cursor-not-allowed disabled:opacity-50 sm:-bottom-1.5 sm:-end-1.5"
                  title={uploadingPhoto ? 'Uploading…' : 'Change photo'}
                >
                  {uploadingPhoto ? (
                    <div
                      className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-purple-500 border-t-transparent sm:h-4 sm:w-4"
                      aria-hidden
                    />
                  ) : (
                    <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.5} aria-hidden />
                  )}
                </button>
                <input
                  id={photoInputId}
                  type="file"
                  aria-label={tProfile('formEdit.uploadPhoto')}
                  accept={photoAccept}
                  onChange={onPhotoChange}
                  className="hidden"
                />
              </>
            ) : null}
          </div>

          <div className="min-w-0 flex-1 text-center sm:text-start">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-950/75 dark:text-violet-100/85">
              {tProfile('profileHero.eyebrow')}
            </p>
            <h2 className="text-lg font-bold tracking-tight text-violet-950 dark:text-white sm:text-xl">
              {displayName}
            </h2>
            <p className="mt-1 truncate text-sm text-violet-950/85 dark:text-violet-100/90 sm:text-base">
              {user?.email}
            </p>
            <p className="mt-0.5 text-xs text-violet-950/75 dark:text-violet-100/75 sm:text-sm">
              {tProfile('profileHero.memberSince', { date: memberDate })}
            </p>
            {showActions ? (
              <LearnerProfileActionButtons
                className="mt-5 md:mt-6"
                onEditProfile={onEditProfile}
                onChangePassword={onChangePassword}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnerProfileHeroCard;
